import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import {
  setAgencyShareChatToastReceived,
  setSelectedCallerID,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { trackViewASCSharedEvent } from '../agencyShareChatTrackings';
import {
  TOAST_EXPIRATION_TIME,
  toastReceived,
  toastUnread,
} from '../utils/agencyShareChatNotificationHelpers';

/**
 * agency share chat toast notifications
 * purple toast notification - a call or sms event has been received by the peer agency
 * blue toast notification - an unread call or sms event for participating agencies
 *
 * @param {function} setShowCallPane - toggles the visibility of the call pane
 * @param {object} theme - styled components theme object
 */
const AgencyShareChatNotifications = withTheme(({ setShowCallPane, theme }) => {
  const dispatch = useDispatch();

  const {
    callSource = null,
    sharerAgencyName = null,
    receivedCallerID = null,
    isToastVisible = false,
  } = useSelector(
    state => state.emergencyCalls?.agencyShareChat?.toastReceived || {},
  );

  const numUnreadCalls = useSelector(
    state => state.emergencyCalls?.agencyShareChat?.unreadCalls?.length || 0,
  );

  // Get conversation ID for the received caller ID
  const conversationId = useSelector(
    state =>
      state.emergencyCalls?.callQueue?.[receivedCallerID]?.agencyShareChat
        ?.conversationID,
  );

  const dismissEventReceived = useCallback(
    () =>
      dispatch(
        setAgencyShareChatToastReceived({
          callSource: null,
          isToastVisible: false,
          receivedCallerID: null,
          sharerAgencyName: null,
        }),
      ),
    [dispatch],
  );

  const handleEventReceived = useCallback(() => {
    // Track viewing the shared event
    if (conversationId && sharerAgencyName) {
      trackViewASCSharedEvent({
        conversationId,
        sharerAgency: sharerAgencyName,
      });
    }

    dispatch(setSelectedCallerID(receivedCallerID));
    setShowCallPane(true);
    dismissEventReceived();
  }, [
    conversationId,
    dismissEventReceived,
    dispatch,
    receivedCallerID,
    setShowCallPane,
    sharerAgencyName,
  ]);

  useEffect(() => {
    if (isToastVisible && receivedCallerID) {
      toastReceived(
        sharerAgencyName,
        receivedCallerID,
        callSource,
        handleEventReceived,
        dismissEventReceived,
        theme,
      );

      const checkToastTimeout = setTimeout(() => {
        dismissEventReceived();
      }, TOAST_EXPIRATION_TIME);

      return () => clearTimeout(checkToastTimeout);
    }
  }, [
    callSource,
    dismissEventReceived,
    handleEventReceived,
    isToastVisible,
    receivedCallerID,
    sharerAgencyName,
    theme,
  ]);

  useEffect(() => {
    if (numUnreadCalls) {
      toastUnread(numUnreadCalls, theme);
    } else if (!numUnreadCalls && toast.isActive('toastUnread')) {
      toast.dismiss('toastUnread');
    }
  }, [numUnreadCalls, theme]);

  return null;
});

AgencyShareChatNotifications.propTypes = {
  setShowCallPane: PropTypes.func.isRequired,
  theme: PropTypes.object,
};

export default AgencyShareChatNotifications;
